import gsap from 'gsap';
import type { ActionData } from '@/types';
import type { Element } from '../../canvas/element';
import { PlayAction } from './PlayAction.class';

export type FlipActionOptions = {
  /** 翻页动画时长(秒)。 */
  flipDuration: number;
  /** 翻页进度渲染回调,由 BookCanvas 注入以驱动 book.flipBetweenPages。 */
  renderProgress: (progress: number) => void;
};

/**
 * 翻页 Action:翻页动画(motion)与翻页音效(element)合一。
 * element = 共享音效 → 复用 PlayAction 的 playTween;
 * flipTween = 翻页动画 → 通过注入的 renderProgress 回调驱动翻页。
 */
export class FlipAction extends PlayAction {
  private flipTween: gsap.core.Tween | null = null;
  // 翻页动画时长,与音效 playTween 的 duration 有意解耦(二者 offset 0 对齐、各自时长独立)。
  private readonly flipDuration: number;
  private readonly renderProgress: (progress: number) => void;

  constructor(data: ActionData, element: Element, options: FlipActionOptions) {
    super(data, element);
    this.flipDuration = options.flipDuration;
    this.renderProgress = options.renderProgress;
  }

  protected override doRefreshMotion(): void {
    super.doRefreshMotion();
    this.initFlipTween();
  }

  private initFlipTween(): void {
    this.destroyFlipTween();

    const state = { progress: 0 };
    // 端点(0/1)渲染交给 onComplete/onReverseComplete,onUpdate 只处理中间进度。
    this.flipTween = gsap.to(state, {
      progress: 1,
      duration: this.flipDuration,
      ease: 'none',
      immediateRender: false,
      onUpdate: () => {
        if (state.progress <= 0 || state.progress >= 1) return;
        this.renderProgress(state.progress);
      },
      onComplete: () => this.renderProgress(1),
      onReverseComplete: () => this.renderProgress(0)
    });

    this._tween?.add(this.flipTween, 0);
  }

  private destroyFlipTween(): void {
    this.flipTween?.kill();
    this.flipTween = null;
  }

  public override destroy(): void {
    this.destroyFlipTween();
    super.destroy();
  }
}
